import { ChangeDetectorRef } from "@angular/core";
import { MatStepper } from "@angular/material/stepper";
import { RouteType } from "src/app/models/enum";
import { DraftItem } from "src/app/models/models";
import { TsRatingFormV2Base } from "../base/ts-rating-form-v2.base";
import { isRemoteSignoffShell } from "../utils/rating-form-version.utils";
import { canSendRemoteSignoff } from "./remote-signoff-send-readiness";

/**
 * Base for TS3 / cabin crew v2 forms with non-TS1 remote sign-off (ratingFormVersion 3).
 * Adds instructor-only send-to-candidate flow matching TS1 v3.
 */
export abstract class RemoteSignoffRatingV2Base extends TsRatingFormV2Base {
	signOffMode: "in_person" | "remote" = "in_person";
	isSendingToCandidate = false;

	protected abstract cd: ChangeDetectorRef;
	protected abstract override rebuildSignOffFormsIfNeeded(): void;

	get isRemoteSignoffShell(): boolean {
		const draft = this.containerRatingsService.draft;
		return isRemoteSignoffShell(
			this.containerRatingsService.formType,
			draft?.ratingFormVersion ?? null,
			draft?.formDetails,
		);
	}

	get canSendToCandidate(): boolean {
		const rows = this.signOffForm?.getRawValue?.() ?? [];
		return canSendRemoteSignoff(this.containerRatingsService.formType, rows) && !this.isSendingToCandidate;
	}

	protected override onStepperSelectionChanged(selectedIndex: number): void {
		const reviewIndex = 2;
		const signOffIndex = this.isRemoteSignoffShell ? 3 : 2;
		if (selectedIndex === reviewIndex || selectedIndex === signOffIndex) {
			this.rebuildSignOffFormsIfNeeded();
		}
	}

	protected override async goToTrainingPage(page: number): Promise<void> {
		this.continuePage = page;

		if (page === 2) {
			await this.onStepperNext(this.stepper);
			return;
		}

		if (page === 3) {
			await this.onStepperNext(this.stepper);
			if (this.isRemoteSignoffShell) {
				this.onStepperNextReview(this.stepper, true);
			} else {
				this.onStepperNextSignOff(this.stepper, true);
			}
			return;
		}

		if (page >= 4 && this.isRemoteSignoffShell) {
			await this.onStepperNext(this.stepper);
			this.onStepperNextReview(this.stepper, true);
			this.onStepperNextSignOff(this.stepper, true);
		}
	}

	/**
	 * Step 2 → review. In-person forms keep their own sign-off transition.
	 */
	onStepperNextReview(_stepper: MatStepper, bypassValidation = false): void {
		if (!bypassValidation) {
			if (this.ratingRootForm.invalid) {
				this.ratingRootForm.markAllAsTouched();
				return;
			}
			void this.saveSignOffDraft();
		}

		this.loadingService.setLoading(true);
		this.rebuildSignOffFormsIfNeeded();
		this.isStep2FormCreated = true;
		this.current = 0;
		this.prev = -1;
		this.continuePage = Math.max(this.continuePage, 3);

		if (bypassValidation) this.forceSelectStep(2);
		else this.stepper.next();

		this.loadingService.setLoading(false);
	}

	/**
	 * Review → sign-off for the remote shell. In-person overrides call `super` only for this branch.
	 */
	onStepperNextSignOff(_stepper: MatStepper, bypassValidation = false): void {
		this.loadingService.setLoading(true);
		this.rebuildSignOffFormsIfNeeded();
		this.isStep2FormCreated = true;
		this.current = 0;
		this.prev = -1;
		this.continuePage = Math.max(this.continuePage, 4);

		if (bypassValidation) this.forceSelectStep(3);
		else this.stepper.next();

		this.loadingService.setLoading(false);
	}

	async onSendToCandidate(_stepper: MatStepper): Promise<void> {
		if (!navigator.onLine) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}

		if (!this.canSendToCandidate || this.isSendingToCandidate) return;
		if (!(await this.confirmSendToCandidateAction())) return;

		this.isSendingToCandidate = true;
		this.cd.markForCheck();
		await new Promise<void>((resolve) => setTimeout(resolve, 0));
		this.signOffMode = "remote";

		try {
			await this.saveSignOffDraft();
			this.containerRatingsService.updateSelectedDraft({ signOffMode: "remote" } as Partial<DraftItem>);

			const signoff = this.signOffForm.at(0).value as {
				trainerName: string;
				instructorLicenseNumber?: string;
				useInstructorLicense?: boolean;
				trainerSignature: File | Blob | string;
			};

			const candidate = this.ratingForm.value as Array<{
				observations: Array<{ traineeAssessmentId?: string }>;
			}>;
			const trainingAssessmentId = (candidate?.[0]?.observations?.[0]?.traineeAssessmentId as string) || "";

			if (!trainingAssessmentId) {
				this.isSendingToCandidate = false;
				this.cd.markForCheck();
				return;
			}

			let instructor_license_number = signoff.instructorLicenseNumber;
			if (!instructor_license_number?.trim()) {
				const useInstructorLicense = signoff.useInstructorLicense ?? true;
				instructor_license_number = useInstructorLicense
					? (this.profileService?.profile?.pilot_license_number ?? "")
					: (this.profileService?.profile?.examiner_number ?? "");
			}

			const draftId = this.containerRatingsService.draft?.id || trainingAssessmentId;
			if (this.containerRatingsService.inDraftMode && draftId) {
				await this.containerRatingsService.flushStagingSync(draftId);
			}

			this.httpService
				.sendToCandidate({
					trainingAssessmentId,
					instructor_name: signoff.trainerName,
					instructor_license_number: String(instructor_license_number ?? "").trim(),
					instructor_signature: signoff.trainerSignature,
				})
				.subscribe({
					next: async () => {
						this.isSendingToCandidate = false;
						this.cd.markForCheck();
						if (this.containerRatingsService.draft?.id) {
							await this.containerRatingsService.deleteDraft(this.containerRatingsService.draft.id);
						}
						this.router.navigate([RouteType.PENDING]);
					},
					error: (err) => {
						this.isSendingToCandidate = false;
						this.cd.markForCheck();
						this.onErrorSignOff(err);
					},
				});
		} catch (err) {
			this.isSendingToCandidate = false;
			this.cd.markForCheck();
			console.error("onSendToCandidate failed:", err);
			this.onErrorSignOff(err);
		}
	}
}
